/**
 * ч.1294 — ширини колонок таблиці з ручкою на межі заголовка.
 *
 * Те саме, що робить `DataTable` для своїх списків, але для таблиць із власною
 * розміткою (таблична частина з полями вводу — розпізнавання виписки): ширини
 * живуть у профілі користувача (`uiPrefs`, ключ `prefKey` — ОБОВʼЯЗКОВО з родини
 * `dt-widths:` / `ag-widths:` / `grid-cols:`: інші ключі uiPrefs не вважає своїми і
 * після перезавантаження не читає), межі 50…700 px,
 * збереження — після відпускання миші. Таблиця малює `<colgroup>` з цих ширин і
 * `table-layout: fixed`, щоб поле не розтягувало колонку під довжину тексту.
 *
 * Використання:
 *   const cw = useColumnWidths('dt-widths:bank-import', { a: 100, b: 200 });
 *   <table className="tch tch--fixed" style={{ minWidth: cw.total }}>
 *     <colgroup>{keys.map((k) => <col key={k} style={{ width: cw.widths[k] }} />)}</colgroup>
 *     <th>… <span className="data-grid__resizer" onMouseDown={(e) => cw.startResize('a', e)} /></th>
 * Клік по заголовку одразу після перетягування — не сортування: `cw.isResizing()`.
 */
import { useCallback, useRef, useState } from 'react';
import type React from 'react';
import uiPrefs from '@/store/uiPrefs';

export type ColumnWidths = Record<string, number>;

export function useColumnWidths(
  prefKey: string,
  defaults: ColumnWidths,
  limits: { min?: number; max?: number } = {},
) {
  if (!/^(dt-widths|ag-widths|grid-cols):/.test(prefKey)) {
    throw new Error(`useColumnWidths: ключ «${prefKey}» поза родинами uiPrefs — ширини не переживуть перезавантаження`);
  }
  const min = limits.min ?? 50;
  const max = limits.max ?? 700;
  const [widths, setWidths] = useState<ColumnWidths>(() => {
    let saved: ColumnWidths | null = null;
    try { saved = uiPrefs.get<ColumnWidths>(prefKey); } catch { saved = null; }
    const out: ColumnWidths = { ...defaults };
    for (const [k, v] of Object.entries(saved ?? {})) {
      if (k in defaults && typeof v === 'number' && v >= min && v <= max) out[k] = v;
    }
    return out;
  });
  const widthsRef = useRef(widths);
  widthsRef.current = widths;
  const resizing = useRef(false);

  const startResize = useCallback((key: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = widthsRef.current[key] ?? defaults[key] ?? min;
    resizing.current = true;
    const onMove = (ev: MouseEvent) => {
      const w = Math.min(max, Math.max(min, Math.round(startW + ev.clientX - startX)));
      setWidths((prev) => (prev[key] === w ? prev : { ...prev, [key]: w }));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      // Зберегти фінальні ширини після завершення drag (актуальний стан —
      // через функціональний setWidths, без застарілого замикання).
      setWidths((cur) => {
        try { uiPrefs.set(prefKey, cur); } catch { /* приватний режим — не критично */ }
        return cur;
      });
      // click по заголовку приходить після mouseup — сортування не вмикати.
      setTimeout(() => { resizing.current = false; }, 0);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [prefKey, defaults, min, max]);

  const reset = useCallback(() => {
    setWidths({ ...defaults });
    try { uiPrefs.remove(prefKey); } catch { /* приватний режим — не критично */ }
  }, [prefKey, defaults]);

  const total = Object.values(widths).reduce((a, b) => a + b, 0);
  const custom = Object.keys(defaults).some((k) => widths[k] !== defaults[k]);
  return { widths, startResize, reset, total, custom, isResizing: () => resizing.current };
}

export default useColumnWidths;
